import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { customersApi } from '@/api/endpoints'
import {
  Empty,
  ErrorNote,
  Loading,
  PageHeader,
  Pagination,
  Toolbar,
} from '@/components/common/Ui'
import { PERMS, can, useAuth } from '@/store/auth'

const PAGE_SIZE = 25

export default function Customers() {
  const { user } = useAuth()
  // Seeded from ?search= so an order can link straight to its customer.
  const [searchParams] = useSearchParams()
  const [search, setSearch] = useState(searchParams.get('search') ?? '')
  const [page, setPage] = useState(1)

  const params = { page, page_size: PAGE_SIZE }
  if (search) params.search = search

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['customers', params],
    queryFn: () => customersApi.list(params),
  })

  const editable = can(user, PERMS.customersWrite)

  return (
    <div className="customers-page">
      <PageHeader
        title="Customers"
        subtitle="The price tier here decides what every order is billed at"
        actions={
          editable && (
            <Link to="/customers/new" className="btn btn-primary btn-sm">
              Add customer
            </Link>
          )
        }
      />

      <Toolbar>
        <input
          type="search"
          placeholder="Name, phone or GSTIN…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
        />
      </Toolbar>

      {isLoading && <Loading />}
      {isError && <ErrorNote message="Could not load customers" onRetry={refetch} />}
      {data && !data.items.length && <Empty title="No customers found" />}

      {data && data.items.length > 0 && (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Business</th>
                <th>WhatsApp</th>
                <th>Tier</th>
                <th>City</th>
                <th>GSTIN</th>
                {editable && <th />}
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.id} className={c.is_active ? '' : 'inactive'}>
                  <td className="strong">
                    <Link to={`/customers/${c.id}`}>{c.name}</Link>
                  </td>
                  <td>{c.business_name ?? '—'}</td>
                  <td className="mono">{c.phone}</td>
                  <td>
                    <span className="badge tier">{c.price_tier}</span>
                  </td>
                  <td>{c.city ?? '—'}</td>
                  <td className="mono">{c.gstin ?? '—'}</td>
                  {editable && (
                    <td className="right">
                      <Link to={`/customers/${c.id}`} className="btn btn-sm">
                        Edit
                      </Link>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          <Pagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            onChange={setPage}
          />
        </>
      )}
    </div>
  )
}
